import * as THREE from "three";
import type { Block, Blocks } from "../blocks";
import type { CameraRig } from "../render/camera";
import type { Hit } from "./act";
import { surfaceTop } from "./surface";

/* =====================================================================
   GRAB TOOL
   The held block is steered with velocities rather than teleported, so it
   still collides: it shoves other blocks aside and they react naturally.
   ===================================================================== */
interface Grab {
  block: Block;
  planeY: number;
  ox: number;
  oz: number;
  tx: number;
  ty: number;
  tz: number;
  lift: number;
  yawOffset: number;
  q0: THREE.Quaternion;
}

const Y_AXIS = new THREE.Vector3(0, 1, 0);

export type GrabTool = ReturnType<typeof createGrabTool>;

export function createGrabTool(blocks: Blocks, rig: CameraRig, canvas: HTMLCanvasElement) {
  const tool = {
    held: null as Grab | null,

    start(hit: Hit) {
      const b = hit.block,
        p = b.pos,
        q = b.quat;
      tool.held = {
        block: b,
        planeY: hit.point.y, // the pointer moves across this height
        ox: p.x - hit.point.x,
        oz: p.z - hit.point.z, // keep the spot you grabbed under the pointer
        tx: p.x,
        ty: p.y,
        tz: p.z, // target position
        lift: 0.25, // gap kept above whatever is underneath
        yawOffset: 0,
        q0: new THREE.Quaternion(q.x, q.y, q.z, q.w), // keep the orientation it had when grabbed
      };
      blocks.wakeAll();
      canvas.style.cursor = "grabbing";
    },

    move(x: number, y: number) {
      const grab = tool.held;
      if (!grab) return;
      const h = rig.screenToPlane(x, y, grab.planeY);
      if (!h) return;
      grab.tx = THREE.MathUtils.clamp(h.x + grab.ox, -14, 14);
      grab.tz = THREE.MathUtils.clamp(h.z + grab.oz, -14, 14);
    },

    lift(d: number) {
      if (tool.held) tool.held.lift = THREE.MathUtils.clamp(tool.held.lift + d, 0.1, 6);
    },

    /** Turns the held block an eighth of a turn (Q/E or the rotate button). */
    turn(dir: number) {
      if (tool.held) tool.held.yawOffset += (dir * Math.PI) / 4;
    },

    end() {
      const grab = tool.held;
      if (!grab) return;
      if (blocks.list.includes(grab.block)) {
        const body = grab.block.body,
          v = body.linvel();
        body.setLinvel({ x: v.x * 0.5, y: v.y * 0.5, z: v.z * 0.5 }, true); // soften the release
        body.setAngvel({ x: 0, y: 0, z: 0 }, true);
      }
      blocks.wakeAll();
      tool.held = null;
      canvas.style.cursor = "";
    },

    steer() {
      const grab = tool.held;
      if (!grab) return;
      if (!blocks.list.includes(grab.block)) {
        tool.held = null;
        canvas.style.cursor = "";
        return;
      }
      const body = grab.block.body,
        a = grab.block.aabb,
        p = grab.block.pos;
      const hx = (a.upperBound.x - a.lowerBound.x) / 2,
        hy = (a.upperBound.y - a.lowerBound.y) / 2,
        hz = (a.upperBound.z - a.lowerBound.z) / 2;

      // Hover just above whatever is underneath, ignoring the held block and
      // anything above its centre (such as blocks riding on top of it); over
      // the void, hold the current height
      const s = surfaceTop(
        blocks.list,
        grab.tx - hx,
        grab.tx + hx,
        grab.tz - hz,
        grab.tz + hz,
        (b) => b !== grab.block && b.aabb.upperBound.y <= p.y,
      );
      if (s !== -Infinity) grab.ty = s + hy + grab.lift;

      // Position: velocity proportional to the distance from the target, capped
      body.wakeUp();
      const v = new THREE.Vector3(grab.tx - p.x, grab.ty - p.y, grab.tz - p.z).multiplyScalar(12);
      if (v.length() > 14) v.setLength(14);
      body.setLinvel({ x: v.x, y: v.y, z: v.z }, true);

      // Rotation: spin toward the target orientation (as grabbed, plus any Q/E turns)
      const target = new THREE.Quaternion()
        .setFromAxisAngle(Y_AXIS, grab.yawOffset)
        .multiply(grab.q0);
      const q = grab.block.quat;
      const err = target.multiply(new THREE.Quaternion(q.x, q.y, q.z, q.w).invert());
      if (err.w < 0) err.set(-err.x, -err.y, -err.z, -err.w);
      const sn = Math.sqrt(Math.max(0, 1 - err.w * err.w));
      if (sn < 1e-4) body.setAngvel({ x: 0, y: 0, z: 0 }, true);
      else {
        const f = (2 * Math.acos(Math.min(1, err.w)) * 10) / sn;
        body.setAngvel({ x: err.x * f, y: err.y * f, z: err.z * f }, true);
      }
    },
  };
  return tool;
}
